import {
  Component,
  Input,
  forwardRef,
  ViewChild,
  ElementRef,
  AfterViewInit
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR
} from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

export interface SelectOption {
  label: string;
  value: string;
}

@Component({
  selector: 'app-searchable-multi-select',
  templateUrl: './searchable-multi-select.component.html',
  styleUrls: ['./searchable-multi-select.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchableMultiSelectComponent),
      multi: true
    }
  ]
})
export class SearchableMultiSelectComponent implements ControlValueAccessor, AfterViewInit {
  @Input() options: SelectOption[] = [];
  @Input() label: string = 'Select options';

  internalValue: string[] = [];
  searchTerm: string = '';
  filtered: SelectOption[] = [];

  private searchSubject = new Subject<string>();
  private touched = false;

  @ViewChild('searchInput') searchInputRef!: ElementRef<HTMLInputElement>;

  ngAfterViewInit(): void {
    this.filtered = [...this.options];
    this.searchSubject.pipe(debounceTime(200)).subscribe(term => {
      const lower = term.toLowerCase();
      this.filtered = this.options.filter(o =>
        o.label.toLowerCase().includes(lower) ||
        o.value.toLowerCase().includes(lower)
      );
    });
  }

  writeValue(value: string[] | null): void {
    this.internalValue = value ?? [];
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  onChange: (value: string[]) => void = () => {};
  onTouched: () => void = () => {};

  markAsTouched(): void {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  onSearchTermChange(term: string): void {
    this.searchTerm = term;
    this.searchSubject.next(term);
  }

  onOpened(): void {
    setTimeout(() => {
      this.searchInputRef?.nativeElement.focus();
    });
  }

  onOptionToggle(value: string, selected: boolean): void {
    if (selected && !this.internalValue.includes(value)) {
      this.internalValue.push(value);
    } else if (!selected) {
      this.internalValue = this.internalValue.filter(v => v !== value);
    }
    this.markAsTouched();
    this.onChange([...this.internalValue]);
  }

  highlightMatch(text: string): string {
    const term = this.searchTerm.trim();
    if (!term) return text;
    const regex = new RegExp(`(${term})`, 'ig');
    return text.replace(regex, '<mark>$1</mark>');
  }
}
